import type { CardSlot, CardSlotView, InventoryInstanceWire } from '@kenocyber/shared';
import {
  CARD_MAX_LEVEL,
  CARD_SLOTS,
  cardArt,
  cardCooldownMsOf,
  cardDefinition,
  cardDescription,
  cardModeNotice,
} from '@kenocyber/shared';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * **A secção "Cartas" do inventário** (gate V28H.G).
 *
 * ## O que ela é, e o que ela deliberadamente não é
 *
 * A coleção, os três espaços e a fusão manual. Não é o HUD de batalha, não tem
 * atalhos, não conta cooldown em tempo real e não dispara habilidade nenhuma -
 * o cooldown que aparece é o que o NÍVEL da carta produz, um número do
 * catálogo, e não um relógio.
 *
 * ## Uma carta por INSTÂNCIA, nunca por definição
 *
 * Duas cópias da mesma carta são duas coisas que o jogador tem, e podem estar em
 * sítios diferentes - uma equipada, outra livre para fundir. Agrupar por
 * definição esconderia exatamente isso.
 *
 * ## O servidor é a autoridade, e esta interface diz isso por palavras
 *
 * Ela desativa o que sabe ser inválido - equipar uma segunda cópia da mesma
 * carta, fundir uma que está equipada - e quando o servidor recusa mesmo assim,
 * mostra a razão DELE. Nunca tenta contornar uma recusa, e nunca inventa uma
 * versão de loadout: a que cada pedido cita é a que veio na última resposta.
 *
 * ## Sem renderer por carta
 *
 * Uma grelha de cartas é uma grelha de `<img>` com molduras de CSS. Não há
 * canvas, não há textura e não há um componente de desenho por carta - a
 * moldura e o brilho saem do tema de raridade que o inventário já usa.
 */

/** De onde a arte servida é lida. A mesma raiz do resto do jogo. */
export const CARD_ASSET_BASE_URL = '/game-assets/';

/** O texto exacto, aprovado, para um bloqueio de duelo. */
export const CARDS_PVP_LOCKED_MESSAGE =
  'As cartas não podem ser alteradas durante uma batalha PvP.';

/** O texto exacto, aprovado, para uma caça a correr. */
export const CARDS_NEXT_HUNT_MESSAGE = 'A nova seleção será usada na próxima Caça.';

/** Os nomes dos três espaços, na ordem em que aparecem. */
export const CARD_SLOT_LABELS: Readonly<Record<CardSlot, string>> = Object.freeze({
  card_slot_1: 'Carta 1',
  card_slot_2: 'Carta 2',
  card_slot_3: 'Carta 3',
});

/**
 * As razões que o servidor pode dar, em português.
 *
 * Um mapa e não um `switch` espalhado: a recusa vem do servidor com um nome
 * estável, e traduzi-la num sítio só é o que impede duas superfícies de darem
 * explicações diferentes para o mesmo `reason`.
 *
 * Uma razão que não esteja aqui cai numa frase genérica em vez de mostrar o
 * identificador ao jogador - `card_already_equipped` não é português.
 */
const FAILURE_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  locked_by_pvp_match: CARDS_PVP_LOCKED_MESSAGE,
  card_already_equipped: 'Esta carta já está equipada em outro espaço.',
  loadout_version_conflict: 'Algo mudou enquanto você decidia. A lista foi recarregada.',
  item_reserved: 'Esta carta está reservada e não pode ser usada agora.',
  item_not_available: 'Esta carta não está disponível.',
  item_not_found: 'Esta carta não foi encontrada.',
  slot_empty: 'Este espaço já está vazio.',
  not_a_card: 'Este item não é uma carta.',
  max_level_reached: 'Esta carta já está no nível máximo.',
  not_combinable: 'Estas duas cartas não podem ser combinadas.',
});

export function cardFailureMessage(reason: string | null): string | null {
  if (reason === null) {
    return null;
  }
  return FAILURE_MESSAGES[reason] ?? 'O servidor recusou a operação.';
}

/**
 * O cooldown, em segundos, como se lê num ecrã.
 *
 * Um decimal só quando ele existe: `8 s` e não `8,0 s`, `3,2 s` e não `3 s`. O
 * número vem da aritmética do catálogo em milissegundos inteiros; isto só o
 * formata.
 */
export function cooldownLabel(milliseconds: number): string {
  const seconds = milliseconds / 1000;
  const text = Number.isInteger(seconds) ? String(seconds) : seconds.toFixed(1).replace('.', ',');
  return `${text} s`;
}

/** O estado em que uma cópia está, por palavras. */
export type CardAvailability = 'free' | 'equipped' | 'reserved' | 'unavailable';

const AVAILABILITY_LABEL: Readonly<Record<CardAvailability, string>> = Object.freeze({
  free: 'Livre',
  equipped: 'Equipada',
  reserved: 'Reservada',
  unavailable: 'Indisponível',
});

export function availabilityOf(item: InventoryInstanceWire): CardAvailability {
  if (item.equipped) {
    return 'equipped';
  }
  if (item.lockedPurpose !== null) {
    return 'reserved';
  }
  if (item.locked) {
    return 'unavailable';
  }
  return 'free';
}

/**
 * A parceira desta cópia para uma fusão, ou `null`.
 *
 * A mesma definição, o MESMO nível, ambas livres, e nunca a própria. Nível 6 não
 * oferece fusão nenhuma - não há nível acima.
 *
 * Determinística: a primeira da lista que serve. A lista chega ordenada do
 * inventário, portanto a escolha não muda entre renders.
 */
export function partnerFor(
  item: InventoryInstanceWire,
  collection: readonly InventoryInstanceWire[],
): InventoryInstanceWire | null {
  if (item.level >= CARD_MAX_LEVEL || availabilityOf(item) !== 'free') {
    return null;
  }
  return (
    collection.find(
      (other) =>
        other.itemId !== item.itemId &&
        other.definitionId === item.definitionId &&
        other.level === item.level &&
        availabilityOf(other) === 'free',
    ) ?? null
  );
}

/* -------------------------------------------------------------------------- */

interface CardTileProps {
  readonly item: InventoryInstanceWire;
  readonly assetBaseUrl: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

/**
 * Uma cópia, na proporção vertical 2:3.
 *
 * A proporção vive no CSS (`aspect-ratio: 2 / 3`) e não numa dimensão em pixels:
 * a grelha encolhe com o painel e a carta continua retrato.
 *
 * O recuo, enquanto os PNGs não existirem, é o genérico de item sem imagem - o
 * mesmo `?` que a célula de inventário desenha. Nunca a arte de uma joia, de um
 * artefacto ou de outro item, e nunca um caminho montado à mão: se o manifesto
 * não tem entrada, não há ficheiro.
 */
function CardTile({ item, assetBaseUrl, selected, onSelect }: CardTileProps) {
  const card = cardDefinition(item.definitionId);
  const art = cardArt(item.definitionId);
  const notice = cardModeNotice(item.definitionId);
  const cooldown = cardCooldownMsOf(item.definitionId, item.level);
  const availability = availabilityOf(item);
  const name = card?.displayName ?? item.definitionId;

  return (
    <button
      type="button"
      className="card-tile"
      data-testid={`card-tile-${item.itemId}`}
      data-rarity={item.rarity}
      data-paint={rarityPaint(item.rarity)}
      data-availability={availability}
      data-selected={selected ? 'true' : 'false'}
      style={rarityStyle(item.rarity)}
      aria-pressed={selected}
      aria-label={`${name}, ${rarityLabel(item.rarity)}, nível ${item.level}, ${AVAILABILITY_LABEL[availability]}`}
      onClick={onSelect}
    >
      <span className="card-tile-art">
        {art ? (
          <img
            src={`${assetBaseUrl}${art.file}`}
            alt={name}
            loading="lazy"
            draggable={false}
            width={art.width}
            height={art.height}
          />
        ) : (
          /*
           * O recuo, e ele é ANUNCIADO.
           *
           * `role="img"` com um nome acessível, e não um `aria-hidden`: quem usa
           * um leitor de ecrã tem de saber que carta é esta, e a ausência da
           * arte não pode tornar a carta invisível.
           */
          <span className="card-tile-noart" role="img" aria-label={`${name} (sem imagem)`}>
            ?
          </span>
        )}
      </span>
      <span className="card-tile-body">
        <strong className="card-tile-name" data-testid={`card-tile-name-${item.itemId}`}>
          {name}
        </strong>
        <span className="card-tile-line">
          <span data-testid={`card-tile-rarity-${item.itemId}`}>{rarityLabel(item.rarity)}</span>
          <span data-testid={`card-tile-level-${item.itemId}`}>Nível {item.level}</span>
        </span>
        {cooldown === null ? null : (
          <span className="card-tile-line" data-testid={`card-tile-cooldown-${item.itemId}`}>
            Recarga {cooldownLabel(cooldown)}
          </span>
        )}
        {notice ? (
          <span className="card-tile-badge" data-testid={`card-tile-pvponly-${item.itemId}`}>
            <strong>{notice.badge}</strong>
            <span className="card-tile-note">{notice.note}</span>
          </span>
        ) : null}
        <span className="card-tile-state" data-testid={`card-tile-state-${item.itemId}`}>
          {AVAILABILITY_LABEL[availability]}
        </span>
        <span className="card-tile-description" data-testid={`card-tile-desc-${item.itemId}`}>
          {cardDescription(item.definitionId) ?? ''}
        </span>
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */

export interface CardsTabProps {
  /** As instâncias de categoria `card` que a conta tem. */
  readonly collection: readonly InventoryInstanceWire[];
  readonly slots: readonly CardSlotView[];
  /*
   * A versão do loadout NÃO é uma propriedade desta aba (gate V28H.H).
   *
   * Ela continua a existir - no estado de `useCards` e citada por cada pedido
   * de equipar, retirar e fundir, que é onde a concorrência é decidida -, mas
   * é um número de protocolo e não diz nada a um jogador. Mostrá-la no rodapé
   * era depuração deixada à vista. As provas de concorrência vivem no teste do
   * gancho, onde o número realmente viaja.
   */
  readonly assetBaseUrl?: string;
  readonly loading?: boolean;
  readonly busy?: boolean;
  readonly error?: string | null;
  /** A razão nomeada da última recusa do servidor. */
  readonly failure?: string | null;
  /** Verdadeiro enquanto há um duelo vivo: as ações ficam desativadas. */
  readonly pvpActive?: boolean;
  /** Verdadeiro enquanto há uma caçada a correr: mostra o aviso. */
  readonly huntActive?: boolean;
  readonly selectedId: string | null;
  readonly onSelect: (itemId: string | null) => void;
  readonly onEquip: (slot: CardSlot, itemId: string) => void;
  readonly onUnequip: (slot: CardSlot) => void;
  readonly onCombine: (firstItemId: string, secondItemId: string) => void;
}

export function CardsTab({
  collection,
  slots,
  assetBaseUrl = CARD_ASSET_BASE_URL,
  loading = false,
  busy = false,
  error = null,
  failure = null,
  pvpActive = false,
  huntActive = false,
  selectedId,
  onSelect,
  onEquip,
  onUnequip,
  onCombine,
}: CardsTabProps) {
  const selected = collection.find((item) => item.itemId === selectedId) ?? null;
  const partner = selected ? partnerFor(selected, collection) : null;
  /*
   * As definições já equipadas: a interface impede visualmente uma segunda
   * cópia da mesma carta, e o servidor recusa-a de qualquer maneira.
   */
  const equippedDefinitions = new Set(
    slots.map((entry) => entry.card?.definitionId).filter((id): id is string => id !== undefined),
  );
  const duplicate =
    selected !== null &&
    equippedDefinitions.has(selected.definitionId) &&
    availabilityOf(selected) === 'free';
  const actionsDisabled = pvpActive || busy || loading;
  const failureMessage = cardFailureMessage(failure ?? null);

  return (
    <section className="cards-tab" data-testid="cards-tab">
      {/*
       * O aviso do duelo vem primeiro, e é um `alert`: ele explica por que
       * todos os botões abaixo estão desativados.
       */}
      {pvpActive ? (
        <p
          className="cards-notice cards-notice-blocked"
          role="alert"
          data-testid="cards-pvp-notice"
        >
          {CARDS_PVP_LOCKED_MESSAGE}
        </p>
      ) : null}
      {!pvpActive && huntActive ? (
        <p className="cards-notice" data-testid="cards-hunt-notice">
          {CARDS_NEXT_HUNT_MESSAGE}
        </p>
      ) : null}
      {error ? (
        <p className="cards-notice cards-notice-blocked" role="alert" data-testid="cards-error">
          {error}
        </p>
      ) : null}
      {failureMessage ? (
        <p className="cards-notice cards-notice-blocked" role="alert" data-testid="cards-failure">
          {failureMessage}
        </p>
      ) : null}

      <h3 className="cards-heading">Espaços</h3>
      <ul className="cards-slots" data-testid="cards-slots">
        {CARD_SLOTS.map((slot) => {
          const entry = slots.find((one) => one.slot === slot) ?? { slot, card: null };
          const occupant = entry.card;
          const definition = occupant ? cardDefinition(occupant.definitionId) : null;
          const notice = occupant ? cardModeNotice(occupant.definitionId) : null;
          const label = CARD_SLOT_LABELS[slot];
          return (
            <li className="cards-slot" key={slot} data-testid={`cards-slot-${slot}`}>
              <h4 className="cards-slot-title">{label}</h4>
              {occupant && definition ? (
                <>
                  <p className="cards-slot-card" data-testid={`cards-slot-card-${slot}`}>
                    <strong>{definition.displayName}</strong>
                    <span>{rarityLabel(definition.rarity)}</span>
                    <span>Nível {occupant.level}</span>
                    <span>
                      {definition.supportedModes.includes('hunt') ? 'Caça e PvP' : 'Somente PvP'}
                    </span>
                    {notice ? <span>{notice.badge}</span> : null}
                  </p>
                  <button
                    type="button"
                    className="cards-slot-action"
                    data-testid={`cards-remove-${slot}`}
                    disabled={actionsDisabled}
                    aria-label={`Remover ${definition.displayName} de ${label}`}
                    onClick={() => onUnequip(slot)}
                  >
                    Remover
                  </button>
                </>
              ) : (
                <p className="cards-slot-empty" data-testid={`cards-slot-empty-${slot}`}>
                  Vazio
                </p>
              )}
              {/*
               * Equipar a carta SELECIONADA neste espaço.
               *
               * Um botão por espaço, e não um menu: os três são intercambiáveis,
               * e o gesto é "põe esta aqui". Substituir é o mesmo botão sobre um
               * espaço ocupado - não há modal, porque nenhum outro espaço deste
               * inventário pede confirmação para uma troca que se desfaz.
               */}
              <button
                type="button"
                className="cards-slot-action"
                data-testid={`cards-equip-${slot}`}
                disabled={actionsDisabled || selected === null || duplicate}
                aria-label={
                  selected
                    ? `${occupant ? 'Substituir por' : 'Equipar'} ${
                        cardDefinition(selected.definitionId)?.displayName ?? selected.definitionId
                      } em ${label}`
                    : `Escolha uma carta para equipar em ${label}`
                }
                onClick={() => {
                  if (selected) {
                    onEquip(slot, selected.itemId);
                  }
                }}
              >
                {occupant ? 'Substituir' : 'Equipar'}
              </button>
            </li>
          );
        })}
      </ul>
      {duplicate ? (
        <p className="cards-notice" role="status" data-testid="cards-duplicate-notice">
          Esta carta já está equipada em outro espaço.
        </p>
      ) : null}

      <h3 className="cards-heading">Coleção</h3>
      {loading ? (
        <p className="cards-empty" data-testid="cards-loading">
          A carregar as cartas…
        </p>
      ) : collection.length === 0 ? (
        <p className="cards-empty" data-testid="cards-empty">
          Você ainda não tem cartas.
        </p>
      ) : (
        <div className="cards-grid" data-testid="cards-grid">
          {collection.map((item) => (
            <CardTile
              key={item.itemId}
              item={item}
              assetBaseUrl={assetBaseUrl}
              selected={item.itemId === selectedId}
              /* Clicar na carta já escolhida desfaz a escolha. */
              onSelect={() => onSelect(item.itemId === selectedId ? null : item.itemId)}
            />
          ))}
        </div>
      )}

      {/*
       * A fusão, e só quando ela é possível.
       *
       * Manual, uma de cada vez: nada funde duplicados por conta do jogador. O
       * botão só existe quando há uma parceira livre da mesma definição e do
       * mesmo nível, e diz o resultado antes de ser carregado.
       */}
      {selected ? (
        <div className="cards-merge" data-testid="cards-merge">
          {partner ? (
            <>
              <p data-testid="cards-merge-preview">
                {`Nível ${selected.level} → Nível ${selected.level + 1}`}
              </p>
              <button
                type="button"
                className="cards-slot-action"
                data-testid="cards-merge-button"
                disabled={actionsDisabled}
                aria-label={`Combinar duas cartas ${
                  cardDefinition(selected.definitionId)?.displayName ?? selected.definitionId
                } de nível ${selected.level}`}
                onClick={() => onCombine(selected.itemId, partner.itemId)}
              >
                Combinar duas cartas
              </button>
            </>
          ) : (
            <p data-testid="cards-merge-unavailable">
              {selected.level >= CARD_MAX_LEVEL
                ? 'Esta carta já está no nível máximo.'
                : availabilityOf(selected) === 'free'
                  ? 'Você não tem outra cópia livre desta carta neste nível.'
                  : 'Uma carta equipada, reservada ou indisponível não pode ser combinada.'}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
